import React, { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
  Icon,
  Spinner // For loading state
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons'; // Or any other pending icon

// Matches the interface in App.tsx
interface ChecklistItem {
  id: string;
  category: string;
  label: string;
  fieldName: string;
  description: string;
  gathered: boolean;
  value?: string;
}

interface ChecklistDisplayProps {
  items: ChecklistItem[];
  isLoading?: boolean; // To show a loading spinner while n8n processes
}

const ChecklistDisplay: React.FC<ChecklistDisplayProps> = ({ items, isLoading }) => {
  const [allExpanded, setAllExpanded] = useState(false);
  const categoryCount = Object.keys(items.reduce((acc: Record<string, boolean>, item) => { acc[item.category] = true; return acc; }, {})).length;
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpandedIndices([]);
      setAllExpanded(false);
    } else {
      setExpandedIndices(Array.from({ length: categoryCount }, (_, i) => i));
      setAllExpanded(true);
    }
  };

  if (!items || items.length === 0) {
    return <Text>No checklist items to display.</Text>;
  }

  // Group items by category
  const itemsByCategory = items.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  return (
    <Box
      w="100%"
      maxW="1200px"
      mx="auto"
      bg="white"
      p={8}
      boxShadow="xl"
      borderRadius="2xl"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={6}>
        <Text fontSize="2xl" fontWeight="extrabold" color="gray.800">✅ Information Checklist</Text>
        <button
          onClick={handleToggleAll}
          style={{
            background: '#3182ce',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 20px',
            fontWeight: 600,
            fontSize: '1rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(49,130,206,0.08)'
          }}
        >
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </Box>
      <Accordion
        allowMultiple
        index={expandedIndices}
        onChange={(val) => setExpandedIndices(Array.isArray(val) ? val : [val])}
        w="100%"
      >
        {Object.entries(itemsByCategory).map(([category, categoryItems], idx) => (
          <AccordionItem key={category} w="100%">
            <h2>
              <AccordionButton w="100%">
                <Box flex="1" textAlign="left" fontWeight="semibold">
                  {category}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4} w="100%">
              <VStack align="stretch" spacing={3} w="100%">
                {categoryItems.map((item) => (
                  <Tooltip key={item.id} label={item.description} placement="top-start" hasArrow>
                    <HStack justifyContent="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }} w="100%">
                      <Text>{item.label}</Text>
                      <HStack>
                        {item.value && (
                          <Text fontSize="sm" color="gray.500" mr={2}>
                            ({item.value})
                          </Text>
                        )}
                        {item.gathered ? (
                          <Icon as={CheckCircleIcon} color="green.500" />
                        ) : (
                          <Icon as={WarningIcon} color="orange.400" />
                        )}
                      </HStack>
                    </HStack>
                  </Tooltip>
                ))}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

export default ChecklistDisplay; 